/**
 * apiFetch — the sanctioned client fetch funnel (Phase 8.4).
 *
 * One wrapper so component call sites stop hand-rolling `fetch("/api/…")` +
 * `res.ok` + `res.json()`. Throws {@link ApiError} on a non-2xx response,
 * carrying the parsed error body when available.
 */
export const ANALYSIS_UPDATE_PENDING_MESSAGE = "Analysis is updating for the active document version. This view will refresh automatically.";
export const ANALYSIS_NOT_READY_CODE = "ANALYSIS_NOT_READY";
const ANALYSIS_UPDATE_PENDING_SERVER_MESSAGES = new Set([
    "File current version is unavailable",
    "File MAIN branch version is unavailable",
    "Document current version is unavailable",
    "Document is not bound to the current file version",
]);
export class AnalysisUpdatePendingError extends Error {
    serverMessage;
    retryAfterMs = 2_000;
    constructor(serverMessage) {
        super(ANALYSIS_UPDATE_PENDING_MESSAGE);
        this.serverMessage = serverMessage;
        this.name = "AnalysisUpdatePendingError";
    }
}
export function isAnalysisUpdatePendingError(error) {
    return error instanceof Error && error.name === "AnalysisUpdatePendingError";
}
function readApiErrorEnvelope(body) {
    if (typeof body !== "object" || body === null)
        return {};
    const envelope = body;
    const nestedError = typeof envelope.error === "object" && envelope.error !== null
        ? envelope.error
        : undefined;
    const code = typeof envelope.code === "string"
        ? envelope.code
        : typeof nestedError?.code === "string"
            ? nestedError.code
            : undefined;
    const message = typeof envelope.message === "string"
        ? envelope.message
        : typeof envelope.error === "string"
            ? envelope.error
            : typeof nestedError?.message === "string"
                ? nestedError.message
                : undefined;
    return { code, message };
}
/**
 * A revision session that is still running answers its review reads with a
 * 425 carrying this code. Like the analysis-pending case above it is a
 * "poll again" signal, not a failure, but it is a separate class so the
 * document-analysis banner text is never shown for a running alignment.
 */
export const REVISION_SESSION_PENDING_MESSAGE = "Alignment is still running.";
export const REVISION_SESSION_PENDING_CODE = "REVISION_SESSION_PENDING";
export class RevisionSessionPendingError extends Error {
    serverMessage;
    retryAfterMs = 2_000;
    constructor(serverMessage) {
        super(REVISION_SESSION_PENDING_MESSAGE);
        this.serverMessage = serverMessage;
        this.name = "RevisionSessionPendingError";
    }
}
export function isRevisionSessionPendingError(error) {
    return error instanceof Error && error.name === "RevisionSessionPendingError";
}
export function isRevisionSessionPendingResponse(status, body) {
    return status === 425 && readApiErrorEnvelope(body).code === REVISION_SESSION_PENDING_CODE;
}
/** Either "poll again" signal; both retry on their own delay instead of failing the query. */
export function isRetryablePendingError(error) {
    return isAnalysisUpdatePendingError(error) || isRevisionSessionPendingError(error);
}
export function isAnalysisUpdatePendingResponse(status, body) {
    const { code, message } = readApiErrorEnvelope(body);
    if (status === 425 && code === ANALYSIS_NOT_READY_CODE)
        return true;
    if (status !== 409)
        return false;
    return message !== undefined && ANALYSIS_UPDATE_PENDING_SERVER_MESSAGES.has(message);
}
export const SERVICE_UNAVAILABLE_MESSAGE = "Service temporarily unavailable";
/** The API is unreachable: the request failed at network level or an HTML error page came back instead of JSON. */
export class ServiceUnavailableError extends Error {
    status;
    constructor(status) {
        super(SERVICE_UNAVAILABLE_MESSAGE);
        this.status = status;
        this.name = "ServiceUnavailableError";
    }
}
export function isServiceUnavailableError(error) {
    return error instanceof Error && error.name === "ServiceUnavailableError";
}
/** True for an HTML body (e.g. the Next.js 404 page), which an API route never legitimately returns. */
export function isHtmlResponse(res) {
    return (res.headers.get("content-type") ?? "").toLowerCase().includes("text/html");
}
/** `fetch` that raises {@link ServiceUnavailableError} on network failure or an HTML response, never embedding the body. */
export async function fetchApi(input, init) {
    let res;
    try {
        res = await fetch(input, init);
    }
    catch {
        throw new ServiceUnavailableError();
    }
    if (isHtmlResponse(res))
        throw new ServiceUnavailableError(res.status);
    return res;
}
/** Error thrown by {@link apiFetch} for any non-2xx response. */
export class ApiError extends Error {
    status;
    body;
    constructor(status, body) {
        const message = readApiErrorEnvelope(body).message ?? `Request failed with status ${status}`;
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}
/**
 * Fetch JSON from an API path. Sets a JSON content-type by default (override via
 * `init.headers`), throws {@link ApiError} on non-2xx, and returns the parsed
 * JSON body typed as `T`.
 */
export async function apiFetch(path, init) {
    const res = await fetchApi(path, {
        ...init,
        headers: { "content-type": "application/json", ...init?.headers },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => undefined);
        const serverMessage = readApiErrorEnvelope(body).message;
        if (isAnalysisUpdatePendingResponse(res.status, body)) {
            throw new AnalysisUpdatePendingError(serverMessage);
        }
        if (isRevisionSessionPendingResponse(res.status, body)) {
            throw new RevisionSessionPendingError(serverMessage);
        }
        throw new ApiError(res.status, body);
    }
    return res.json();
}
